import { Injectable, inject } from '@angular/core';
import { getApp } from 'firebase/app';
import {
  Analytics,
  getAnalytics,
  logEvent,
  setAnalyticsCollectionEnabled,
  setUserId,
  setUserProperties,
} from 'firebase/analytics';
import { EventName } from './event-names';
import { captureStackTrace, normalizeError } from 'src/app/shared/errors/error-utilities';
import { PlatformInfoService } from '../platform-service/platform-info.service';
import { environment } from 'src/environments/environment';
import { SentryService } from './sentry.service';
import { OperatorService } from '../operator/operator.service';
import { OperatorMessenger } from '../operator/operator-messenger';

@Injectable({
  providedIn: 'root',
})
export class AnalyticsService {
  private platformInfoService = inject(PlatformInfoService);
  private sentryService = inject(SentryService);
  private isInitialised = false;
  private analytics?: Analytics;

  initialise(): void {
    if (OperatorService.IsDemo) {
      return;
    }

    this.analytics = getAnalytics(getApp());

    if (this.isInitialised) {
      throw new Error('AnalyticsService should only be initialised once.');
    }
    this.isInitialised = true;

    setAnalyticsCollectionEnabled(this.analytics, true);
  }

  signIn(userId: string, operatorId: string): void {
    if (!this.isInitialised) {
      return;
    }

    setUserId(this.getAnalytics(), userId);
    this.sentryService.setUserId(userId);
    this.sentryService.setOperator(operatorId);
  }

  log(name: EventName, params: object = {}): void {
    if (!this.initialised()) {
      return;
    }
    logEvent(this.getAnalytics(), name, params);
    this.sentryService.addBreadcrumb(name, params);
    OperatorMessenger.sendAnalyticsEventToOperator(name);
  }

  logBreadcrumb(name: string, params: object = {}): void {
    if (!this.initialised()) {
      return;
    }
    this.sentryService.addBreadcrumb(name, params);
  }

  async handleError<T extends Record<string, unknown>>(
    err: unknown,
    context?: string,
    data?: T,
  ): Promise<void> {
    if (!this.initialised()) {
      return;
    }

    // Convert into an Error
    const error = normalizeError(err);

    // Capture the exception with Sentry
    this.sentryService.captureException(error, context, data);

    console.error(error.message);

    // Capture full stack trace
    const [stackTrace, _rawStackFrames] = await captureStackTrace(error);

    const currentVersion = environment.version;
    const build = environment.build;

    // Log analytics
    const params = {
      message: error.message,
      name: error.name,
      build: build,
      ...(currentVersion && { version: currentVersion }),
      ...(context && { context: context }),
      ...data,
      ...(error.stack && { stack: error.stack }),
      ...(stackTrace && { stackTrace: stackTrace }),
    };

    const platform = this.platformInfoService.getPlatform();
    const eventName = `${platform}_error_object`;
    logEvent(this.getAnalytics(), eventName, params);

    console.error(`${JSON.stringify(params)}`);
  }

  errorWarning(error: string): void {
    if (!this.initialised()) {
      return;
    }

    console.warn(error);
    const params = {
      message: error,
    };

    const platform = this.platformInfoService.getPlatform();
    const eventName = `${platform}_error_warning`;
    logEvent(this.getAnalytics(), eventName, params);
  }

  setProperty(key: string, value: string) {
    if (!this.initialised()) {
      return;
    }

    setUserProperties(this.getAnalytics(), { [key]: value });
  }

  private initialised(): boolean {
    return this.analytics !== undefined && this.isInitialised;
  }

  private getAnalytics(): Analytics {
    if (this.analytics === undefined) {
      throw new Error('AnalyticsService is not initialised');
    }
    return this.analytics;
  }
}
